// ============================================================================
// /redefinir-senha/:token — confirmação de reset via link do email
// ============================================================================
// User chega aqui pelo link que recebeu no email. Token vem na URL.
// Aqui ele escolhe a nova senha, validamos via POST /api/auth-reset-confirm.
//
// Mesma estética dark da tela de login.
// ============================================================================

import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, Check, AlertTriangle, KeyRound, LogIn } from 'lucide-react';
import DNALogo from '../components/DNALogo.jsx';

function FKLogoLogin() {
  return (
    <div className="flex justify-center">
      <DNALogo tone="light" width={200} />
    </div>
  );
}

function checkStrength(pwd) {
  if (!pwd || pwd.length < 8) return { ok: false, msg: 'Mínimo 8 caracteres' };
  if (!/[a-zA-Z]/.test(pwd)) return { ok: false, msg: 'Precisa de pelo menos 1 letra' };
  if (!/[0-9]/.test(pwd)) return { ok: false, msg: 'Precisa de pelo menos 1 número' };
  return { ok: true };
}

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const strength = checkStrength(pwd);
  const match = pwd === confirm;
  const canSubmit = strength.ok && match && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!strength.ok) { setError(strength.msg); return; }
    if (!match) { setError('As senhas não conferem'); return; }

    setLoading(true);
    try {
      const r = await fetch('/api/auth-reset-confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: pwd }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(data?.error || 'Não foi possível trocar a senha. Tente novamente.');
        setLoading(false);
        return;
      }
      setDone(true);
      // Auto-redirect pro login em 4s
      setTimeout(() => navigate('/login'), 4000);
    } catch (err) {
      setError('Erro de conexão. Tenta de novo.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1B2D] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[#41659E]/[0.12] rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm animate-fade-in">
        <div className="text-center mb-10">
          <FKLogoLogin />
        </div>

        <div className="bg-[#16243A] border border-[#23344F] rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-brand-300/15 border border-brand-300/20 flex items-center justify-center">
              <KeyRound size={18} className="text-brand-300" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#E9EEF5]">Redefinir senha</h2>
            </div>
          </div>

          {done ? (
            <div className="mt-4 space-y-4">
              <div className="bg-green-950/40 border border-green-900/50 rounded-xl px-4 py-4 text-green-400 text-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Check size={16} />
                  <span className="font-semibold">Senha redefinida!</span>
                </div>
                <p className="text-green-400/80 text-xs leading-relaxed">
                  Você já pode entrar com a nova senha. Todas as sessões antigas foram encerradas.
                  Um email de confirmação foi enviado pra você.
                </p>
                <p className="text-green-400/60 text-xs mt-2">Redirecionando…</p>
              </div>
              <Link
                to="/login"
                className="w-full bg-gradient-to-r from-[#41659E] to-[#29426F] text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-all text-sm tracking-wide flex items-center justify-center gap-2"
              >
                <LogIn size={16} /> Ir agora pro login
              </Link>
            </div>
          ) : (
            <>
              <p className="text-[#5B6C86] text-xs mb-6 mt-3">
                Escolha uma senha forte: mínimo 8 caracteres, com letras e números.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-[#8C9CB5] mb-1.5">Nova senha</label>
                  <div className="relative">
                    <input
                      type={show ? 'text' : 'password'}
                      value={pwd}
                      onChange={e => { setPwd(e.target.value); setError(''); }}
                      placeholder="••••••••"
                      required
                      autoFocus
                      className="w-full bg-[#0A1322] border border-[#23344F] text-[#E9EEF5] rounded-xl px-4 py-3 pr-12 text-sm focus:outline-none focus:border-brand-300 focus:ring-1 focus:ring-brand-300/20 transition-all placeholder-[#5B6C86]"
                    />
                    <button
                      type="button"
                      onClick={() => setShow(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5B6C86] hover:text-[#8C9CB5]"
                      tabIndex={-1}
                    >
                      {show ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {pwd && !strength.ok && (
                    <p className="text-amber-400 text-xs mt-1.5">{strength.msg}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-[#8C9CB5] mb-1.5">Confirmar senha</label>
                  <input
                    type={show ? 'text' : 'password'}
                    value={confirm}
                    onChange={e => { setConfirm(e.target.value); setError(''); }}
                    placeholder="••••••••"
                    required
                    className="w-full bg-[#0A1322] border border-[#23344F] text-[#E9EEF5] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-300 focus:ring-1 focus:ring-brand-300/20 transition-all placeholder-[#5B6C86]"
                  />
                  {confirm && !match && (
                    <p className="text-amber-400 text-xs mt-1.5">As senhas não conferem</p>
                  )}
                </div>

                {error && (
                  <div className="bg-red-950/40 border border-red-900/50 rounded-xl px-4 py-3 text-red-400 text-xs flex items-start gap-2">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full bg-gradient-to-r from-[#41659E] to-[#29426F] text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-all disabled:opacity-30 disabled:cursor-not-allowed text-sm tracking-wide flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Salvando...</>
                  ) : (
                    <><Lock size={16} /> Trocar senha</>
                  )}
                </button>

                <Link
                  to="/login"
                  className="block text-center text-[#8C9CB5] text-xs hover:text-brand-300 transition-colors pt-2"
                >
                  Voltar ao login
                </Link>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-[#5B6C86] text-xs mt-6">
          Não pediu esse reset? Ignore esse link e nada vai mudar.
        </p>
      </div>
    </div>
  );
}
